import React from "react";
import { motion } from "framer-motion";
import { Plane, Hotel, Bus, Gift } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { APP_SCREENSHOTS } from "@/lib/constants";

const PERKS = [
  { icon: Plane, label: "Flights", desc: "One-way and round trips with senior-citizen & student fares." },
  { icon: Hotel, label: "Hotels", desc: "Boutique stays to 5-star chains, with GST invoice for work travel." },
  { icon: Bus, label: "Intercity Bus", desc: "Search 4 lakh+ routes with clean fare comparison." },
  { icon: Gift, label: "Partner Perks", desc: "Times Prime, Tata Cliq, Croma, Realme, Oppo and more." },
];

const TRAVEL_SCREENS = [
  { src: APP_SCREENSHOTS.flights, label: "Flights" },
  { src: APP_SCREENSHOTS.hotels, label: "Hotels" },
  { src: APP_SCREENSHOTS.buses, label: "Buses" },
];

const PhoneFrame = ({ src, alt }) => (
  <div className="relative w-full aspect-[9/19]">
    <div className="absolute inset-0 rounded-[2rem] bg-[#0A0A0A] p-2 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.4)]">
      <div className="relative h-full w-full rounded-[1.7rem] bg-white overflow-hidden">
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 h-3.5 w-16 bg-[#0A0A0A] rounded-full z-10" />
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      </div>
    </div>
  </div>
);

export const Travel = () => {
  return (
    <section
      id="travel"
      data-testid={LANDING.travelSection}
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F25C2A]">
            · Travel &amp; Lifestyle
          </p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#0A0A0A]">
            Book flights, hotels &amp; <em className="italic text-[#F25C2A] font-normal">buses</em>
            <br />
            in the same app.
          </h2>
          <p className="mt-5 max-w-xl text-lg text-[#4A4A4A] leading-relaxed">
            EMDpay&apos;s marketplace also covers your travel essentials — clean search,
            transparent pricing and fares that compete with leading travel platforms.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-3 gap-4 lg:gap-8 max-w-3xl mx-auto">
          {TRAVEL_SCREENS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
            >
              <PhoneFrame src={s.src} alt={`EMDpay ${s.label} screen`} />
              <p className="mt-4 text-center text-xs uppercase tracking-[0.22em] font-semibold text-[#4A4A4A]">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {PERKS.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className="bg-white border border-[#E5E5E0] rounded-2xl p-5"
            >
              <p.icon size={20} className="text-[#F25C2A]" />
              <p className="mt-4 font-serif text-lg text-[#0A0A0A]">{p.label}</p>
              <p className="mt-1 text-sm text-[#4A4A4A] leading-relaxed">
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
